<template>
  <div class="view-personal-data">
    <div class="container-card rounded-16 bg-2 p-6 mb-7">
      <div class="text-center my-9" v-if="loadingInfo">
        <b-spinner variant="primary" />
      </div>

      <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h6>
            Perfil pessoal
          </h6>

          <div class="d-flex gap-3">
            <snow-button
              size="sm"
              variant="gray"
              text="Cancelar"
              @click="loadInfo"
            />
            <snow-button
              text="Salvar"
              size="sm"
              :loading="loading"
              :disabled="!isValid || !isAllowEdit"
              @click="submit"
            />
          </div>
        </div>

        <div v-if="!isAllowEdit" class="rounded-16 bg-1 p-4 mb-4">
          <div class="d-flex gap-2">
            <i class="ph ph-warning-circle fs-20" />
            <div class="d-flex flex-column">
              <span class="text-dark fs-14">
                Atenção
              </span>
              <span class="text-dark-40 fs-12">
                Para editar essas informações, entre em contato com o suporte
              </span>
            </div>
          </div>
        </div>

        <div class="row align-items-start mb-4">
          <div class="col-lg-12 mb-4">
            <select-person-type
              v-if="form.name"
              v-model="form.type_account"
              :disabled="!isAllowEdit"
              @input="form.document = ''"
            />
          </div>
          <div class="col-lg-6">
            <snow-input
              v-model="form.name"
              label="Seu nome ou razão social da empresa"
              :rules="rules.name"
              :disabled="!isAllowEdit"
            />
          </div>

          <div class="col-lg-6">
            <snow-input
              v-model="form.email"
              readonly
              type="email"
              label="E-mail"
            />
          </div>

          <template v-if="form.type_account == 'PF'">
            <div class="col-lg-6">
              <snow-input
                v-model="form.document"
                mask="###.###.###-##"
                label="Digite seu CPF"
                :rules="rules.cpf"
                :disabled="!isAllowEdit"
              />
            </div>
            <div class="col-lg-6">
              <snow-input
                v-model="form.birth_date"
                masked
                mask="##/##/####"
                label="Data de nascimento"
                :disabled="!isAllowEdit"
              />
            </div>
          </template>

          <template v-else>
            <div class="col-lg-6">
              <snow-input
                v-model="form.document"
                label="Digite seu CNPJ"
                mask="##.###.###/####-##"
                :rules="rules.cnpj"
                :disabled="!isAllowEdit"
              />
            </div>
          </template>
        </div>

        <template v-if="user.company.token">
          <h6 class="mb-4">Token</h6>

          <div class="row mb-4">
            <div class="col-lg-6">
              <snow-input readonly v-model="user.company.token">
                <snow-button
                  size="sm"
                  variant="borderless"
                  icon="ph ph-copy"
                  @click="copyToken"
                />
              </snow-input>
            </div>
          </div>
        </template>

        <h6 class="mb-4">Endereço completo</h6>

        <div class="row">
          <div class="col-lg-4">
            <snow-select
              label="País de residência"
              v-model="form.country"
              :options="countryOptions"
              :disabled="!isAllowEdit"
              @input="onChangeCountry"
            />
          </div>
          <div class="col-lg-4">
            <snow-input
              v-model="form.zipcode"
              label="CEP"
              mask="#####-###"
              :delay="500"
              :disabled="!isAllowEdit"
              @change="getZipcode(form.zipcode)"
            />
          </div>
          <div class="col-lg-4">
            <snow-select
              v-if="form.country == 30"
              label="Estado"
              v-model="form.state"
              :options="stateOptions"
              :disabled="loadZipcodeInfo || !isAllowEdit"
              @input="onChangeState"
            />
          </div>

          <div class="col-lg-4">
            <snow-input
              v-model="form.address"
              label="Endereço"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <snow-input
              v-model="form.number"
              type="number"
              label="Número"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <snow-input
              v-model="form.complement"
              label="Complemento"
              :disabled="!isAllowEdit"
            />
          </div>

          <div class="col-lg-6">
            <snow-select
              v-if="form.country == 30"
              label="Cidade/Município"
              v-model="form.city"
              :options="cityOptions"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
            <snow-input
              v-else
              v-model="form.city"
              label="Cidade/Município"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
          <div class="col-lg-6">
            <snow-input
              v-model="form.neighborhood"
              label="Bairro"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
        </div>

        <h6 class="mb-4">Telefones</h6>

        <div class="d-flex flex-column gap-4 flex-xl-row">
          <div class="row w-100">
            <div class="col-6 col-xl-3">
              <snow-input
                v-model="currentDialCode"
                readonly
                label="Cód. País"
              />
            </div>
            <div class="col-6 col-xl-3">
              <snow-input
                v-model="form.phone.ddd"
                mask="##"
                label="DDD"
                :disabled="!isAllowEdit"
              />
            </div>
            <div class="col-xl-6">
              <snow-input
                v-model="form.phone.number"
                mask="# ####-####"
                label="Telefone"
                :disabled="!isAllowEdit"
              />
            </div>
          </div>

          <div class="row w-100">
            <div class="col-6 col-xl-3">
              <snow-input
                v-model="currentDialCode"
                readonly
                label="Cód. País"
              />
            </div>
            <div class="col-6 col-xl-3">
              <snow-input
                v-model="form.phone_01.ddd"
                mask="##"
                label="DDD"
                :disabled="!isAllowEdit"
              />
            </div>
            <div class="col-xl-6">
              <snow-input
                v-model="form.phone_01.number"
                mask="# ####-####"
                label="Telefone"
                :disabled="!isAllowEdit"
              />
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="container-card rounded-16 bg-2 p-6">
      <div class="text-center my-9" v-if="loadingInfo">
        <b-spinner variant="primary" />
      </div>

      <change-password v-else />
    </div>
  </div>
</template>
<script>
import SnowInput from "@/components/snow-ui/form/SnowInput.vue";
import SnowButton from "@/components/snow-ui/SnowButton.vue";

import ChangePassword from "@/components/user/PersonalData/ChangePassword.vue";
import SelectPersonType from "@/components/user/PersonalData/SelectPersonType";

import { COUNTRIES_LIST } from "@/constants/countries.js";
import { STATES_CITIES_LIST } from "@/constants/statesCities.js";
import { formFields, isValidCNPJ, isValidCPF } from "@/functions/helpers";

import { required } from "@/functions/validation";

import axios from "axios";
import moment from "moment";
import { mapState } from "vuex";
import user from "../../store/user";

export default {
  components: {
    SelectPersonType,
    ChangePassword,
    SnowButton,
    SnowInput
  },
  data() {
    return {
      ...formFields(
        [
          "type_account",
          "name",
          "email",
          "document",
          "birth_date",
          "country",
          "zipcode",
          "state",
          "address",
          "number",
          "complement",
          "city",
          "neighborhood",
          "phone",
          "phone_01"
        ],
        {
          phone: {
            country: "",
            ddd: "",
            number: ""
          },
          phone_01: {
            country: "",
            ddd: "",
            number: ""
          }
        }
      ),
      loadingInfo: false,
      loadZipcodeInfo: false,
      isAllowEdit: true,
      rules: {
        name: [required()],
        cpf: [required(), val => isValidCPF(val) || "CPF inválido."],
        cnpj: [required(), val => isValidCNPJ(val) || "CNPJ inválido."]
      }
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    countryOptions() {
      return COUNTRIES_LIST.map((item, index) => {
        return { id: index + 1, name: item.label, dialCode: item.dialCode };
      });
    },
    stateOptions() {
      return STATES_CITIES_LIST.map((item, index) => {
        return {
          id: index + 1,
          name: item.nome,
          uf: item.sigla,
          cities: item.cidades
        };
      });
    },
    cityOptions() {
      return this.form.state
        ? this.stateOptions
            .filter(item => item.id == this.form.state)[0]
            .cities.map((item, index) => {
              return { id: index + 1, name: item };
            })
        : [];
    },
    currentDialCode() {
      return "+55";
    },
    isValid() {
      return (
        this.form.type_account &&
        this.form.name &&
        this.form.email &&
        this.form.document &&
        (this.form.type_account == "PJ" ||
          (this.form.birth_date && this.form.birth_date.length == 10)) &&
        (this.form.type_account == "PJ"
          ? isValidCNPJ(this.form.document)
          : isValidCPF(this.form.document)) &&
        this.form.country &&
        this.form.zipcode &&
        (this.form.state || this.form.country != 30) &&
        this.form.address &&
        this.form.number &&
        this.form.city &&
        this.form.neighborhood &&
        this.form.phone.ddd &&
        this.form.phone.ddd.length == 2 &&
        this.form.phone.number
      );
    }
  },
  methods: {
    copyToken() {
      navigator.clipboard.writeText(this.user.company.token);
      this.$message.success("Token copiado");
    },
    getZipcode(val) {
      if (this.form.country == 30 && val.length == 8) {
        this.loadZipcodeInfo = true;
        const instanceAx = axios.create({ headers: {} });
        instanceAx.defaults.headers = null;
        instanceAx
          .get(`https://viacep.com.br/ws/${val}/json/`)
          .then(({ data }) => {
            this.form.state = this.stateOptions.find(
              item => item.uf == data.uf
            ).id;
            setTimeout(() => {
              this.form.city = this.cityOptions.find(
                item => item.name.toLowerCase() == data.localidade.toLowerCase()
              ).id;
            }, 1);
            this.form.neighborhood = data.bairro;
            this.form.address = data.logradouro;
          })
          .finally(() => (this.loadZipcodeInfo = false));
      }
    },
    onChangeCountry() {
      this.form.state = "";
      this.form.city = "";
    },
    onChangeState() {
      this.form.city = "";
    },

    submit() {
      this.loading = true;
      const data = {
        ...this.form
      };

      if (this.form.type_account == "PJ") delete data.birth_date;

      data.country = this.countryOptions.find(
        item => item.id == this.form.country
      ).name;

      data.state = this.stateOptions.find(
        item => item.id == this.form.state
      )?.uf;

      if (typeof this.form.city == "number") {
        data.city = this.cityOptions.find(
          item => item.id == this.form.city
        ).name;
      }

      data.phone =
        this.currentDialCode + this.form.phone.ddd + this.form.phone.number;
      data.phone_01 =
        this.currentDialCode +
        this.form.phone_01.ddd +
        this.form.phone_01.number;

      data.phone_02 = "";

      this.$store
        .dispatch("user/update", data)
        .then(() => {
          this.$message.success("Salvo com sucesso");
        })
        .catch(error => {
          this.errors = error.response.data;
          this.$message.error("Erro ao salvar");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInfo() {
      this.loadingInfo = true;
      this.$store
        .dispatch("user/getInfo")
        .then(data => {
          this.form = {
            ...this.form,
            ...data,
            type_account: data.type_account || "PF"
          };

          this.isAllowEdit = data.is_allow_edit;

          if (data.birth_date)
            this.form.birth_date = moment(data.birth_date).format("DD/MM/YYYY");

          this.form.country = data.country
            ? this.countryOptions.find(opt => opt.name == data.country)?.id
            : 30;

          this.form.state =
            this.form.country == 30 && data.state
              ? this.stateOptions.find(opt => opt.uf == data.state)?.id
              : "";

          this.form.city =
            this.form.country == 30 && this.form.state && data.city
              ? this.cityOptions.find(opt => opt.name == data.city)?.id
              : data.city;

          if (data.phone) {
            this.form.phone =
              data.phone[0] == "+"
                ? {
                    ddd: data.phone.substring(3, 5),
                    number: data.phone.substring(5)
                  }
                : {
                    ddd: data.phone.substring(2, 4),
                    number: data.phone.substring(4)
                  };
          } else {
            this.form.phone = {
              ddd: "",
              number: ""
            };
          }

          if (data.phone_01) {
            this.form.phone_01 =
              data.phone_01[0] == "+"
                ? {
                    ddd: data.phone_01.substring(3, 5),
                    number: data.phone_01.substring(5)
                  }
                : {
                    ddd: data.phone_01.substring(2, 4),
                    number: data.phone_01.substring(4)
                  };
          } else {
            this.form.phone_01 = {
              ddd: "",
              number: ""
            };
          }
        })
        .finally(() => {
          this.loadingInfo = false;
        });
    }
  },
  watch: {
    "form.confirmPass": function(val) {
      if (val !== this.form.newPass) {
        this.errors.confirmPass = ["As senhas são diferentes"];
      } else {
        this.errors.confirmPass = [];
      }
    }
  },
  mounted() {
    this.loadInfo();
  }
};
</script>
<style scoped lang="scss">
.view-personal-data {
  h6 {
    font-size: 14px;
    font-weight: 600;
    color: var(--dark);
  }

  .row > * {
    margin-bottom: 16px;
  }
}
</style>
